// Audio Waveform Visualizer for Remotion Podcast Composition
// Animated equalizer bars that respond to the active speaker

import React from "react";
import { useCurrentFrame, useVideoConfig, interpolate } from "remotion";

interface AudioVisualizerProps {
  isActive: boolean;
  activeHostIndex: number;
  colors: Array<{
    primary: string;
    glow: string;
    bg: string;
  }>;
}

const BAR_COUNT = 7;

// Deterministic pseudo-random based on frame and index
function pseudoRandom(seed: number): number {
  const x = Math.sin(seed * 12.9898 + seed * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  isActive,
  activeHostIndex,
  colors,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const time = frame / fps;

  const color = colors[activeHostIndex] || colors[0];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        height: 80,
        width: 60,
      }}
    >
      {Array.from({ length: BAR_COUNT }).map((_, i) => {
        // Use multiple sine waves at different frequencies for organic motion
        const speed1 = 3 + i * 0.7;
        const speed2 = 5 + i * 1.1;
        const speed3 = 2 + i * 0.4;
        const phase = i * 0.8;

        const wave1 = Math.sin(time * speed1 + phase) * 0.3;
        const wave2 = Math.sin(time * speed2 + phase + 1.5) * 0.25;
        const wave3 = Math.sin(time * speed3 + phase + 3.0) * 0.15;

        // Combine waves for natural-looking height variation
        const rawHeight = isActive
          ? 0.35 + wave1 + wave2 + wave3
          : 0.1 + Math.sin(time * 0.5 + phase) * 0.05;

        // Clamp between 0.08 and 1.0
        const normalizedHeight = Math.max(0.08, Math.min(1.0, rawHeight));
        const barHeight = normalizedHeight * 70;

        // Color intensity based on height
        const opacity = isActive
          ? interpolate(normalizedHeight, [0.1, 0.8], [0.5, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            })
          : 0.3;

        return (
          <div
            key={i}
            style={{
              width: 5,
              height: barHeight,
              borderRadius: 3,
              backgroundColor: color.primary,
              opacity,
              boxShadow: isActive
                ? `0 0 ${8 * normalizedHeight}px ${color.glow}60`
                : "none",
              transition: "height 0.08s ease-out",
            }}
          />
        );
      })}
    </div>
  );
};

export default AudioVisualizer;
